import React, { ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import Button from '../../../atoms/buttons/Button';
import PhoneNumberInput from '../../../atoms/PhoneNumberInput';
import { useApp } from '../../../contexts/AppContext';
import { FormWrapper, ButtonAction, DashlineSeparator, ErrorMessage, StyledEditablePhoneInput } from './styledElements';
import { isQAautomation, validatePhoneNumber } from '../../../utils/helpers';
import useGeolocationData from '../../../hooks/useGeolocationData';
import { tomtomEnabled } from '../../../config/Master';
import { handlePhoneSettingsVysionEvent } from '../../../utils/vysion/phoneSettings';
import { VYSION_ACTIONS } from '../../../utils/vysion';
import { PhoneSettingsClickVysionEventsEnum } from '../../../utils/vysion/phoneSettings/click';
import { TwilioVerbiage } from '../TwilioVerbiage';
import { HCaptchaContainer } from '../../common/CommonStyle';
import HCaptcha from '../../auth/Hcaptcha';
import { RootState } from '../../../store';

interface IAddPhoneInputProps {
  allowChange?: boolean;
  profileCountry?: string;
  nextStep?: (value: string, captcha?: string) => void;
}

const AddPhoneInput = ({ profileCountry, nextStep }: IAddPhoneInputProps): ReactElement => {
  const [newPhoneValue, setNewPhoneValue] = useState<string>('');
  const { hasRequestOtpError, setHasRequestOtpError } = useApp();
  const { countryCode } = useGeolocationData(tomtomEnabled);
  const [captcha, setCaptcha] = useState('');

  const { is_member_hcaptcha_enabled, cf_country } = useSelector((state: RootState) => state.settings);

  const isButtonDisabled = (): boolean => {
    if (isQAautomation()) {
      return false;
    }

    return (is_member_hcaptcha_enabled && isEmpty(captcha)) || !newPhoneValue;
  };

  const handleSubmitPhone = () => {
    handlePhoneSettingsVysionEvent({
      action: VYSION_ACTIONS.CLICK,
      event: PhoneSettingsClickVysionEventsEnum.CLICK_CHANGE_PHONE_NUMBER_UPDATE,
    });

    if (!validatePhoneNumber(newPhoneValue)) {
      setHasRequestOtpError('The phone number format is invalid.');
      return;
    }

    if (typeof nextStep === 'function') {
      nextStep(newPhoneValue, captcha);
    }
  };

  return (
    <FormWrapper data-test-id="add_phone_settings_container">
      <DashlineSeparator style={{ marginTop: '0px', marginBottom: '20px', border: 0 }} />
      <StyledEditablePhoneInput>
        <PhoneNumberInput
          dataTestId="text-add-phone"
          defaultCountry={countryCode ?? 'us'}
          customLabelStyle={{ fontWeight: 600, fontSize: 18 }}
          customStyle={{ width: '100%' }}
          label="Current Phone Number"
          value={newPhoneValue}
          autoFocus
          onChange={value => setNewPhoneValue(value)}
          onFocus={() => setHasRequestOtpError('')}
          disabled={false}
          handleOnChangeCountry={country => {
            handlePhoneSettingsVysionEvent({
              action: VYSION_ACTIONS.CLICK,
              event: PhoneSettingsClickVysionEventsEnum.CLICK_CHANGE_PHONE_NUMBER_COUNTRY,
              extraInfo: country.name,
              newEventName: `accountMenu-changePhoneNumber-countryCode-${country.countryCode}`,
            });
          }}
          handleClickCountryDropdown={showDropdown => {
            if (showDropdown) {
              handlePhoneSettingsVysionEvent({
                action: VYSION_ACTIONS.CLICK,
                event: PhoneSettingsClickVysionEventsEnum.CLICK_CHANGE_PHONE_NUMBER_COUNTRY_CODE,
              });
            }
          }}
        >
          {hasRequestOtpError && <ErrorMessage>{hasRequestOtpError}</ErrorMessage>}
        </PhoneNumberInput>
      </StyledEditablePhoneInput>
      <DashlineSeparator />
      <TwilioVerbiage profileCountry={profileCountry} />
      {is_member_hcaptcha_enabled && (
        <>
          <DashlineSeparator />
          <HCaptchaContainer style={{ marginTop: 20 }}>
            <HCaptcha
              cfCountry={cf_country}
              storeCaptchaResponse={token => setCaptcha(token)}
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              resetCaptcha={hasRequestOtpError}
            />
          </HCaptchaContainer>
        </>
      )}
      <DashlineSeparator style={{ margin: '20px 0 0' }} />
      <ButtonAction>
        <Button
          data-test-id="phone-settings-button"
          buttonType="primaryRed"
          onClick={handleSubmitPhone}
          type={'button'}
          disabled={isButtonDisabled()}
          size="small"
        >
          Update
        </Button>
      </ButtonAction>
    </FormWrapper>
  );
};

export default AddPhoneInput;
